import type { Membership } from '../../../lib/api';

/**
 * Per-tier Vortex graduation rewards. One row per tier; each tier's rewards
 * are stored as a nested `vortex` object on its `memberships.config`
 * (`reward_cash`, `reward_gc`, `activation_token_grant`, `reentry_token_grant`).
 * Direct-recruit tokens are NOT here — they're a sponsor×recruit membership
 * matrix (`computation.vortex_direct_token`) edited below this table.
 * Lives in the ComPlan -> Vortex tab alongside the flat queue/graduation
 * config, matching how direct/indirect per-tier matrices are edited.
 */
const FIELDS = [
  ['reward_cash', 'Reward cash'],
  ['reward_gc', 'Reward GC'],
  ['activation_token_grant', 'Activation tokens'],
  ['reentry_token_grant', 'Re-entry tokens'],
] as const;

export function VortexRewardsPerTier({
  memberships,
  draft,
  onChange,
  readOnly,
}: {
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] text-muted-foreground">
        Paid to a slot of each tier when its Vortex entry graduates. Blank = none. The tenant-wide
        token requirement and graduation N are set in the Vortex config above.
      </p>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Tier</th>
              <th className="px-3 py-2 font-medium">Name</th>
              {FIELDS.map(([, label]) => (
                <th key={label} className="px-3 py-2 font-medium">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {memberships.map((m) => {
              const current = draft[m.id] ?? {};
              const vortex = (current.vortex ?? {}) as Record<string, unknown>;
              return (
                <tr key={m.id} className="border-t">
                  <td className="px-3 py-1.5 text-xs font-mono">{m.code}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{m.name}</td>
                  {FIELDS.map(([key]) => {
                    const raw = vortex[key];
                    const value =
                      typeof raw === 'number' || typeof raw === 'string' ? Number(raw) : 0;
                    return (
                      <td key={key} className="px-3 py-1.5">
                        <input
                          type="number"
                          className="h-8 w-28 rounded-md border bg-background px-2 text-xs disabled:opacity-50"
                          value={value}
                          disabled={readOnly}
                          step={0.01}
                          min={0}
                          onChange={(e) => {
                            const n = Number(e.target.value) || 0;
                            const nextVortex = { ...vortex };
                            if (n === 0) delete nextVortex[key];
                            else nextVortex[key] = n;
                            const next = { ...current };
                            if (Object.keys(nextVortex).length > 0) next.vortex = nextVortex;
                            else delete next.vortex;
                            onChange(m.id, next);
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
